import React from 'react';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../../utils/formatters';
import LoadingSpinner from '../../ui/LoadingSpinner';

// Financial summary component for return process
const FinancialSummary = ({ 
  orderItems = [], 
  shippingCost, 
  currency = 'USD',
  isLoadingShipping = false
}) => {
  // Calculate total product price from all items
  const productPrice = React.useMemo(() => {
    return orderItems.reduce((sum, item) => {
      return sum + (parseFloat(item.price) || 0) * (item.quantity || 1);
    }, 0);
  }, [orderItems]);
  
  // Get usage fee (usually same for all items in a return)
  const usageFee = orderItems[0]?.returnRequest?.usageFee || 0;
  
  // Calculate final amounts with shipping cost
  const finalAmounts = React.useMemo(() => {
    // Convert to numbers to ensure proper math
    const productPriceNum = parseFloat(productPrice) || 0;
    const usageFeeNum = parseFloat(usageFee) || 0;
    const shippingCostNum = parseFloat(shippingCost) || 0;
    
    // For returns: 
    // - If productPrice > (usageFee + shippingCost), customer gets refund
    // - If (usageFee + shippingCost) > productPrice, customer pays difference
    const total = usageFeeNum + shippingCostNum - productPriceNum;
    
    return {
      total,
      isRefund: total < 0,
      absoluteTotal: Math.abs(total),
    };
  }, [productPrice, usageFee, shippingCost]);
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="flex items-center mb-4">
        {/* <CurrencyDollarIcon className="h-5 w-5 text-gray-500 mr-2" /> */}
        <h3 className="text-lg font-medium">Financial Summary</h3>
      </div>
      
      <div className="space-y-2">
        {/* Product Price */}
        <div className="flex justify-between items-center py-2">
          <span className="text-gray-700">Product Price</span>
          <span className="text-gray-700">{formatCurrency(productPrice, currency)}</span>
        </div>
        
        {/* Usage Fee (if applicable) */}
        {usageFee > 0 && (
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-700">Usage Fee</span>
            <span className="text-gray-700">-{formatCurrency(usageFee, currency)}</span>
          </div>
        )}
        
        {/* Shipping Cost */}
        <div className="flex justify-between items-center py-2">
          <span className="text-gray-700">Shipping</span>
          {isLoadingShipping ? (
            <div className="flex items-center text-gray-500">
              <LoadingSpinner size="xs" className="mr-2" />
              <span>Calculating...</span>
            </div>
          ) : shippingCost !== undefined && shippingCost !== null ? (
            <span className="text-gray-700">-{formatCurrency(shippingCost, currency)}</span>
          ) : (
            <span className="text-gray-500 italic">Select address to calculate</span>
          )}
        </div>
      
        {/* Final Total/Refund */}
        <div className="flex justify-between items-center font-medium text-lg mt-4 pt-4 border-t border-gray-200">
          {isLoadingShipping ? (
            <>
              <span className="text-gray-700">Total</span>
              <div className="flex items-center text-gray-500">
                <LoadingSpinner size="xs" className="mr-2" />
                <span>Calculating...</span>
              </div>
            </>
          ) : (
            <>
              <span className="text-gray-800">
                {finalAmounts.isRefund ? 'You Will Receive:' : 'You Will Pay:'}
              </span>
              <span className={finalAmounts.isRefund ? 'text-green-600' : 'text-red-600'}>
                {finalAmounts.isRefund && '+'}{formatCurrency(finalAmounts.absoluteTotal, currency)}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialSummary; 